import { Block, Flex, Input, Typography } from '@aloft/toolkit-sui';

import { Controller, useWatch } from 'react-hook-form';

import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import { RecommendCompsFormSchema } from '../../types.ts';
import { filterHasValue } from '../../utils/filterHasValue.ts';
import { EMPTY_RANGE_FILTER, FilterSection, WithinRangeMode } from './FilterSection.tsx';
import { SteppedPercentInput } from './SteppedPercentInput.tsx';

const MODE_TO_MATCH_CRITERIA: Record<WithinRangeMode, 'value_range' | 'min_max_range'> = {
  within: 'value_range',
  range: 'min_max_range',
};

export const YearBuiltFilter = ({ subjectYearBuilt }: { subjectYearBuilt?: number }) => {
  const { control, setValue } = useZodFormContext<RecommendCompsFormSchema>();
  const matchCriteria = useWatch({ control, name: 'search.matchCriteria.yearBuilt' });
  const filterValue = useWatch({ control, name: 'search.filters.yearBuilt' });
  const mode: WithinRangeMode = matchCriteria === 'min_max_range' ? 'range' : 'within';

  const handleModeChange = (nextMode: WithinRangeMode) => {
    setValue('search.filters.yearBuilt', { ...EMPTY_RANGE_FILTER });
    setValue('search.matchCriteria.yearBuilt', MODE_TO_MATCH_CRITERIA[nextMode]);
  };

  return (
    <FilterSection
      label="Year Built"
      mode={mode}
      onModeChange={handleModeChange}
      showClear={filterHasValue(filterValue)}
      onClear={() => setValue('search.filters.yearBuilt', { ...EMPTY_RANGE_FILTER })}
    >
      {mode === 'within' ? (
        <Flex alignItems="center" gap="12px" minWidth={0} width="100%">
          <Flex alignItems="center" height="40px" flex="1 1 0" minWidth={0}>
            <Typography variant="body2" color="textPrimary">
              {subjectYearBuilt ?? ''}
            </Typography>
          </Flex>
          <Block flex="1 1 0" minWidth={0}>
            <Controller
              name="search.filters.yearBuilt.variance"
              control={control}
              render={({ field }) => (
                <SteppedPercentInput
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  step={1}
                  unit="yrs"
                />
              )}
            />
          </Block>
        </Flex>
      ) : (
        <Flex gap="8px" minWidth={0} width="100%">
          <Block flex="1 1 0" minWidth={0}>
            <Controller
              name="search.filters.yearBuilt.min"
              control={control}
              render={({ field }) => (
                <Input
                  id="yearBuilt-min"
                  size="sm"
                  type="number"
                  placeholderText="No min"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onClear={() => field.onChange('')}
                />
              )}
            />
          </Block>
          <Block flex="1 1 0" minWidth={0}>
            <Controller
              name="search.filters.yearBuilt.max"
              control={control}
              render={({ field }) => (
                <Input
                  id="yearBuilt-max"
                  size="sm"
                  type="number"
                  placeholderText="No max"
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  onClear={() => field.onChange('')}
                />
              )}
            />
          </Block>
        </Flex>
      )}
    </FilterSection>
  );
};
